import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Text } from "@core/index";
import { useGetPublicAssignment } from "@packages/api/madlenAPI";
import { configureAxios } from "../../../axiosConfig";

type PublicItem = { id: string; text: string } | string;

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(";").shift() || null;
  return null;
}

function setCookie(name: string, value: string, days = 180) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/`;
}

async function convertBlobToWav16kMono16bit(inputBlob: Blob): Promise<Blob> {
  const TARGET_SAMPLE_RATE = 16000;
  const TARGET_CHANNELS = 1;

  const arrayBuffer = await inputBlob.arrayBuffer();
  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
  const decodeCtx = new AudioCtx();
  const decodedBuffer: AudioBuffer = await new Promise((resolve, reject) => {
    decodeCtx.decodeAudioData(
      arrayBuffer.slice(0),
      (buf) => resolve(buf),
      (err) => reject(err),
    );
  });
  await decodeCtx.close();

  const duration = decodedBuffer.duration;
  const length = Math.ceil(duration * TARGET_SAMPLE_RATE);
  const offlineCtx = new OfflineAudioContext(
    TARGET_CHANNELS,
    length,
    TARGET_SAMPLE_RATE,
  );
  const source = offlineCtx.createBufferSource();
  source.buffer = decodedBuffer;
  source.connect(offlineCtx.destination);
  source.start(0);
  const rendered: AudioBuffer = await offlineCtx.startRendering();
  const channelData = rendered.getChannelData(0);

  // Encode WAV PCM 16-bit LE
  const bytesPerSample = 2;
  const blockAlign = TARGET_CHANNELS * bytesPerSample;
  const byteRate = TARGET_SAMPLE_RATE * blockAlign;
  const dataSize = channelData.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  function writeString(offset: number, str: string) {
    for (let i = 0; i < str.length; i += 1) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }

  // RIFF header
  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");

  // fmt subchunk
  writeString(12, "fmt ");
  view.setUint32(16, 16, true); // PCM header length
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, TARGET_CHANNELS, true);
  view.setUint32(24, TARGET_SAMPLE_RATE, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // bits per sample

  // data subchunk
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  // PCM samples
  let offset = 44;
  for (let i = 0; i < channelData.length; i += 1) {
    let s = channelData[i];
    if (s < -1) s = -1;
    if (s > 1) s = 1;
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([view], { type: "audio/wav" });
}

const PublicPronunciationPage = () => {
  const { t } = useTranslation();
  const params = useParams();
  const code = params.code as string | undefined;

  const [solverName, setSolverName] = useState<string>("");
  const [uploadingItemIds, setUploadingItemIds] = useState<
    Record<string, boolean>
  >({});
  const [recordingItemId, setRecordingItemId] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  const [submissionsByItem, setSubmissionsByItem] = useState<
    Record<string, any[]>
  >({});

  // MediaRecorder state
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  const [recordedBlobByItem, setRecordedBlobByItem] = useState<
    Record<string, Blob | null>
  >({});
  const mimeTypeRef = useRef<string>("audio/webm;codecs=opus");
  const timerRef = useRef<number | null>(null);
  const pendingSaveItemIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!code) return;
    const saved = getCookie(`pron_solver_${code}`);
    if (saved) setSolverName(decodeURIComponent(saved));
  }, [code]);

  useEffect(() => {
    if (!code) return;
    if (solverName && solverName.trim().length > 0) {
      setCookie(`pron_solver_${code}`, solverName.trim());
    }
  }, [code, solverName]);

  const {
    data: publicAssignment,
    isLoading,
    isError,
  } = useGetPublicAssignment(code || "", {
    query: { enabled: !!code },
  });

  const items: { id: string; text: string }[] = useMemo(() => {
    const raw: any = (publicAssignment as any)?.items;
    if (!raw || !Array.isArray(raw)) return [];
    return raw
      .map((it: PublicItem, idx: number) => {
        if (typeof it === "string") return { id: String(idx + 1), text: it };
        if (it && typeof (it as any).text === "string" && (it as any).id)
          return { id: String((it as any).id), text: (it as any).text };
        if (it && typeof (it as any).text === "string")
          return { id: String(idx + 1), text: (it as any).text };
        return null;
      })
      .filter(Boolean) as { id: string; text: string }[];
  }, [publicAssignment]);

  // Eğer görev detayında mevcut sonuçlar varsa başlangıçta yükle
  useEffect(() => {
    const existingResults = (publicAssignment as any)?.results;
    if (existingResults && typeof existingResults === "object") {
      setSubmissionsByItem(existingResults as Record<string, any[]>);
    }
  }, [publicAssignment]);

  function startTimer() {
    const startedAt = Date.now();
    setElapsedMs(0);
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 100);
  }

  function stopTimer() {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function chooseSupportedMimeType(): string {
    const candidates = ["audio/ogg;codecs=opus", "audio/webm"];
    for (const c of candidates) {
      // @ts-ignore
      if (
        typeof MediaRecorder !== "undefined" &&
        MediaRecorder.isTypeSupported &&
        MediaRecorder.isTypeSupported(c)
      ) {
        return c;
      }
    }
    return "";
  }

  async function startRecording(itemId: string) {
    // Stop current recording if active
    if (recorderRef.current && recorderRef.current.state === "recording") {
      try {
        recorderRef.current.stop();
      } catch {}
    }
    setRecordingItemId(itemId);
    setElapsedMs(0);
    recordedChunksRef.current = [];
    pendingSaveItemIdRef.current = null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = chooseSupportedMimeType();
      mimeTypeRef.current = mime || "audio/webm";
      const recorder = new MediaRecorder(
        stream,
        mime ? { mimeType: mime } : undefined,
      );

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        stopTimer();
        const itemIdToSave = pendingSaveItemIdRef.current || recordingItemId;
        const finalBlob = new Blob(recordedChunksRef.current, {
          type: mimeTypeRef.current,
        });
        if (itemIdToSave) {
          setRecordedBlobByItem((prev) => ({
            ...prev,
            [itemIdToSave]: finalBlob,
          }));
        }
        pendingSaveItemIdRef.current = null;
      };

      recorderRef.current = recorder;
      // timeslice ile düzenli chunk üretimi (bazı tarayıcılarda kritik)
      recorder.start(250);
      startTimer();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Microphone permission error", err);
      cleanupRecording();
      setRecordingItemId(null);
    }
  }

  function stopRecording(saveToItemId?: string) {
    const itemId = saveToItemId || recordingItemId;
    pendingSaveItemIdRef.current = itemId || null;

    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setRecordingItemId(null);
  }

  function cleanupRecording() {
    stopTimer();
    if (recorderRef.current && recorderRef.current.state === "recording") {
      try {
        recorderRef.current.stop();
      } catch {}
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recordedChunksRef.current = [];
    pendingSaveItemIdRef.current = null;
  }

  async function handleUpload(file: File, itemId: string) {
    if (!code || !solverName || solverName.trim().length === 0) return;
    setUploadingItemIds((s) => ({ ...s, [itemId]: true }));
    try {
      const formData = new FormData();
      // filename ekleyin ki backend ve provider doğru tespit etsin
      formData.append("audio", file, file.name || "recording.wav");
      formData.append("solverName", solverName.trim());
      formData.append("itemId", itemId);

      const res = await configureAxios<any>({
        url: `/swann-pronunciation/public/${code}/submit`,
        method: "POST",
        data: formData,
        // axios'ın JSON'a çevirip Content-Type'ı application/json yapmasını engelle
        transformRequest: [
          (data: any, headers: any) => {
            // tüm seviyelerde olası Content-Type zorlamalarını kaldır
            if (headers?.common) delete headers.common["Content-Type"];
            if (headers?.post) delete headers.post["Content-Type"];
            delete headers["Content-Type"];
            return data; // FormData'yı olduğu gibi gönder
          },
        ],
        // Burada headers belirtmeyin. Axios boundary'yi kendisi ekler.
        // headers: { "Content-Type": "multipart/form-data" } EKLEMEYİN
      });
      // Yanıtı state'e işle
      setSubmissionsByItem((prev) => {
        const next = { ...prev } as Record<string, any[]>;
        // Tüm sonuçlar haritası dönerse doğrudan kullan
        if (res && typeof res === "object" && res.results) {
          // results: { [itemId]: Submission[] }
          return res.results as Record<string, any[]>;
        }
        // Tek submission dönerse ilgili item'a ekle
        const arr = Array.isArray(next[itemId]) ? next[itemId] : [];
        return { ...next, [itemId]: [...arr, res] };
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Submission failed", e);
    } finally {
      setUploadingItemIds((s) => ({ ...s, [itemId]: false }));
    }
  }

  async function handleSend(itemId: string) {
    const blob = recordedBlobByItem[itemId];
    if (!blob) return;
    try {
      // Önerilen kalite: WAV 16k mono 16-bit
      const wavBlob = await convertBlobToWav16kMono16bit(blob);
      const file = new File([wavBlob], "recording.wav", { type: "audio/wav" });
      await handleUpload(file, itemId);
    } catch (err) {
      // Fallback: dönüşüm başarısız olursa orijinalini gönder
      const extension = mimeTypeRef.current.includes("ogg") ? "ogg" : "webm";
      const file = new File([blob], `recording.${extension}`, {
        type: mimeTypeRef.current || blob.type || "audio/webm",
      });
      await handleUpload(file, itemId);
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!code) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Text variant="h2">{t("pronunciationPublic.error")}</Text>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Text variant="p">{t("pronunciationPublic.loading")}</Text>
      </div>
    );
  }

  if (isError || !publicAssignment) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
          <Text variant="h2" className="mb-2">
            {t("pronunciationPublic.error")}
          </Text>
          <Text variant="p">{t("pronunciationPublic.errorNotFound")}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-500 p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
          <Text variant="h1" className="text-2xl font-semibold mb-1">
            {publicAssignment?.title || t("pronunciationPublic.title")}
          </Text>
          {publicAssignment?.description && (
            <Text variant="p" className="text-gray-600 dark:text-gray-400">
              {publicAssignment.description}
            </Text>
          )}
        </div>

        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
          <Text variant="h2" className="text-lg font-semibold mb-3">
            {t("pronunciationPublic.enterName")}
          </Text>
          <input
            type="text"
            value={solverName}
            onChange={(e) => setSolverName(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-dark-500 dark:border-dark-400 dark:text-white"
            placeholder={t("pronunciationPublic.enterName") || ""}
          />
        </div>

        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
          <Text variant="h2" className="text-lg font-semibold mb-4">
            {t("pronunciationPublic.itemsTitle")}
          </Text>
          {items.length === 0 ? (
            <Text variant="p">{t("pronunciationPublic.noItems")}</Text>
          ) : (
            <div className="space-y-4">
              {items.map((it, idx) => (
                <div
                  key={it.id}
                  className="flex flex-col gap-3 p-3 border rounded-lg dark:border-dark-400"
                >
                  {/* Header row */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center text-sm font-semibold">
                      {idx + 1}
                    </div>
                    <div className="flex-1 text-gray-900 dark:text-gray-100">
                      {it.text}
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                      {recordingItemId === it.id && (
                        <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                          <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
                          <span className="font-mono text-sm">
                            {new Date(elapsedMs)
                              .toISOString()
                              .substring(14, 19)}
                          </span>
                        </div>
                      )}

                      {recordingItemId === it.id ? (
                        <button
                          type="button"
                          onClick={() => stopRecording(it.id)}
                          className="inline-flex items-center justify-center px-4 py-2 rounded-full transition-all border text-sm font-medium shadow-sm bg-red-600 hover:bg-red-700 text-white border-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          {t("pronunciationPublic.stopRecording")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={!solverName || uploadingItemIds[it.id]}
                          onClick={() => startRecording(it.id)}
                          className="inline-flex items-center justify-center px-4 py-2 rounded-full transition-all border text-sm font-medium shadow-sm bg-cyan-600 hover:bg-cyan-700 text-white border-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
                        >
                          {t("pronunciationPublic.startRecording")}
                        </button>
                      )}

                      <button
                        type="button"
                        disabled={
                          !recordedBlobByItem[it.id] || uploadingItemIds[it.id]
                        }
                        onClick={() => handleSend(it.id)}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-full transition-all border text-sm font-medium shadow-sm bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
                      >
                        {t("pronunciationPublic.send")}
                      </button>
                    </div>
                  </div>

                  {/* Latest result section */}
                  {(() => {
                    const submissions = submissionsByItem[it.id] || [];
                    const last = submissions.length
                      ? submissions[submissions.length - 1]
                      : null;
                    const result = last?.result;
                    if (!result) return null;
                    const metricChip = (
                      label: string,
                      value: string | number | undefined,
                      color = "bg-gray-100 text-gray-700 dark:bg-dark-500 dark:text-gray-200",
                    ) => (
                      <div
                        className={`px-2 py-1 rounded-md text-xs font-medium ${color}`}
                      >
                        <span className="opacity-70 mr-1">{label}:</span>
                        <span>{value ?? "-"}</span>
                      </div>
                    );
                    return (
                      <div className="mt-1 border-t pt-3 dark:border-dark-400">
                        <div className="flex items-center justify-between mb-2">
                          <Text variant="h3" className="text-sm font-semibold">
                            {t("pronunciationPublic.latestResult")}
                          </Text>
                          <div className="flex flex-wrap gap-2">
                            {metricChip(
                              t("pronunciationPublic.overall"),
                              result.overall,
                              "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
                            )}
                            {metricChip(
                              t("pronunciationPublic.pronunciation"),
                              result.pronunciation,
                              "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
                            )}
                            {metricChip(
                              t("pronunciationPublic.fluency"),
                              result.fluency,
                              "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
                            )}
                            {metricChip(
                              t("pronunciationPublic.rhythm"),
                              result.rhythm,
                            )}
                            {metricChip(
                              t("pronunciationPublic.speed"),
                              result.speed,
                            )}
                            {metricChip(
                              t("pronunciationPublic.duration"),
                              result.duration || result.numeric_duration,
                            )}
                            {metricChip(
                              t("pronunciationPublic.integrity"),
                              result.integrity,
                            )}
                            {metricChip(
                              t("pronunciationPublic.pauseCount"),
                              result.pause_count,
                            )}
                            {metricChip(
                              t("pronunciationPublic.rearTone"),
                              result.rear_tone,
                            )}
                          </div>
                        </div>

                        {/* Words */}
                        {Array.isArray(result.words) &&
                          result.words.length > 0 && (
                            <div>
                              <Text
                                variant="h4"
                                className="text-xs font-semibold mb-2"
                              >
                                {t("pronunciationPublic.words")}
                              </Text>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {result.words.map((w: any, wi: number) => (
                                  <div
                                    key={`${w.word}-${wi}`}
                                    className="border rounded-md px-3 py-2 dark:border-dark-400"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="font-medium text-gray-900 dark:text-gray-100">
                                        {w.word}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {metricChip(
                                          t("pronunciationPublic.score"),
                                          w.scores?.overall ??
                                            w.scores?.pronunciation,
                                        )}
                                        {typeof w.scores?.pronunciation ===
                                          "number" &&
                                          metricChip(
                                            t(
                                              "pronunciationPublic.pronunciation",
                                            ),
                                            w.scores?.pronunciation,
                                          )}
                                      </div>
                                    </div>
                                    {Array.isArray(w.phonemes) &&
                                      w.phonemes.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                          {w.phonemes.map(
                                            (p: any, pi: number) => (
                                              <span
                                                key={pi}
                                                className="px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-800 dark:bg-dark-500 dark:text-gray-200"
                                                title={`${t("pronunciationPublic.pronunciation")}: ${p.pronunciation ?? "-"}`}
                                              >
                                                /{p.phoneme}/{" "}
                                                {typeof p.pronunciation ===
                                                "number"
                                                  ? `(${p.pronunciation})`
                                                  : ""}
                                              </span>
                                            ),
                                          )}
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicPronunciationPage;
